import React, { useState, useEffect } from "react";
import {
  collection, doc, query, addDoc, deleteDoc, where,
  onSnapshot, setDoc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, app } from "../firebase";
import {
  beschreibeGesamtsync, gesamtsyncZeitpunkt,
  gesamtsyncBilanz, formatBilanzGruppe,
} from "./syncStatus";

// ── Stage constants ────────────────────────────────────────────────────────────

// Wie die Alters-Stufe des letzten Gesamtsyncs aussieht. Die Regel dahinter
// steht in syncStatus.js und ist dort getestet; hier nur die Darstellung.
const GESAMTSYNC_FARBEN = {
  frisch:    { punkt: "#48bb78", text: "#276749" },
  alt:       { punkt: "#ecc94b", text: "#975a16" },
  veraltet:  { punkt: "#fc8181", text: "#c53030" },
  unbekannt: { punkt: "#cbd5e0", text: "#a0aec0" },
};

const GESAMTSYNC_ERKLAERUNG =
  "Gesamtsync = alle sechs Stufen werden auf Etsy abgeglichen (Knopf " +
  "„Allgemein“).\n\n" +
  "GEFUNDEN zeigt, was nur dieser Lauf findet: eine Stufe, die auf Etsy von " +
  "Hand verschoben wurde, eine Bestellung, die es nur dort gibt, eine dort " +
  "abgeschlossene Bestellung.\n\n" +
  "NEBENBEI zeigt, was er zwar mit erledigt, was aber ohne ihn auch " +
  "passiert: Stufenwechsel, die wir ausgelöst haben, und private Notizen. " +
  "Das übernehmen die kleinen automatischen Syncs alle paar Minuten.\n\n" +
  "Etsy-Nachrichten stehen in keiner der beiden Zeilen — die laufen " +
  "vollständig unabhängig von jedem Sync, alle paar Sekunden.\n\n" +
  "Steht bei GEFUNDEN „nichts“, hätte dieser Lauf auch entfallen können.";

const GesamtsyncZeile = ({ datum, jetzt, bilanz }) => {
  const { stufe, text } = beschreibeGesamtsync(datum, jetzt);
  const farbe = GESAMTSYNC_FARBEN[stufe] || GESAMTSYNC_FARBEN.unbekannt;
  const b = bilanz || {};
  const fundText = b.fundeErfasst
    ? (b.nichtsGefunden ? "nichts" : formatBilanzGruppe(b.funde))
    : null;
  const arbeitText = b.arbeitErfasst ? formatBilanzGruppe(b.arbeit) : "";

  return (
    <span
      title={GESAMTSYNC_ERKLAERUNG}
      style={{
        display: "flex", flexDirection: "column", alignItems: "flex-end",
        gap: "1px", fontSize: "0.78rem", cursor: "help", lineHeight: 1.35,
      }}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", gap: "6px",
        color: farbe.text,
      }}>
        <span style={{
          width: "7px", height: "7px", borderRadius: "50%",
          background: farbe.punkt, flexShrink: 0,
        }} />
        {text}
      </span>

      {/* „Gefunden: nichts" ist die eigentliche Aussage — deshalb steht sie
          da, auch wenn alle Zahlen null sind. Weggelassen wird die Zeile nur,
          wenn gar keine Bilanz erfasst ist (z. B. bei den am 04.10.2026 aus
          der Historie nachgetragenen Werten): eine 0 zu zeigen hiesse dort,
          eine Messung zu behaupten, die nie stattgefunden hat. */}
      {fundText !== null && (
        <span style={{ color: b.nichtsGefunden ? "#a0aec0" : "#276749" }}>
          Gefunden: {fundText}
        </span>
      )}
      {arbeitText && (
        <span style={{ color: "#a0aec0" }}>
          Nebenbei: {arbeitText} — läuft auch ohne
        </span>
      )}
    </span>
  );
};

const STAGE_KEYS = [
  "new", "grafiker", "entwurf_raus", "produktion", "druck", "kerzen",
  "test", "archived",
];

const STAGE_LABELS = {
  new:          "Neu",
  grafiker:     "Grafiker",
  entwurf_raus: "Entwurf raus",
  produktion:   "Produktion",
  druck:        "Druck",
  kerzen:       "Kerzen",
  test:         "Test",
  archived:     "Archiviert",
};

const STAGE_COLORS = {
  new:          { background: "#fefcbf", color: "#744210", border: "1px solid #f6e05e" },
  grafiker:     { background: "#bee3f8", color: "#2a4365", border: "1px solid #63b3ed" },
  entwurf_raus: { background: "#e9d8fd", color: "#44337a", border: "1px solid #b794f4" },
  produktion:   { background: "#feebc8", color: "#7b341e", border: "1px solid #f6ad55" },
  druck:        { background: "#c6f6d5", color: "#1c4532", border: "1px solid #68d391" },
  kerzen:       { background: "#fed7e2", color: "#702459", border: "1px solid #f687b3" },
  test:         { background: "#faf5ff", color: "#553c9a", border: "1px solid #b794f4" },
  archived:     { background: "#edf2f7", color: "#718096", border: "1px solid #cbd5e0" },
};

// „Grafiker (auto)": kraeftiges Blau statt des hellen, damit die wenigen
// automatischen Auftraege in einer Liste voller manueller sofort auffallen.
// Heute ist das Verhaeltnis so herum — von 57 Auftraegen in Grafiker hatte am
// 19.09.2026 genau einer eine Vorlage im Personalization Tool.
const AUTO_STAGE_COLOR = {
  background: "#2b6cb0", color: "#ffffff", border: "1px solid #2c5282",
};

// Stufen, in denen die Marke etwas aendert: davor entscheidet die Grafikerin,
// ob sie selbst gestaltet. Ab „Entwurf raus" ist der Entwurf beim Kunden, dann
// sagt „auto" nichts mehr, was nicht ohnehin sichtbar waere.
const AUTO_RELEVANT_STAGES = new Set(["new", "grafiker"]);

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatIsoDate = (iso) => {
  if (!iso) return "—";
  // Works for both "YYYY-MM-DD" (old orders) and "YYYY-MM-DDTHH:MM:SSZ" (new)
  const [y, m, d] = iso.split("T")[0].split("-");
  return `${d}.${m}.${y}`;
};

const isUrgent = (shipByDate) => {
  if (!shipByDate) return false;
  return new Date(shipByDate) <= new Date(Date.now() + 2 * 86400000);
};

/**
 * Wie viele Positionen einer Bestellung das Personalization Tool automatisch
 * vorbereiten kann — erkannt an der Artikelnummer, NICHT am Kandidaten.
 *
 * Gemessen am 19.09.2026: keiner der 57 Auftraege in „Grafiker" hatte einen
 * Kandidaten. Der entsteht erst beim Rendern, und mit der Freigabe verlaesst
 * der Auftrag die Stufe wieder — in dem Moment, in dem die Grafikerin ihre
 * Liste durchsieht, sagt er also nichts. Die Vorlage sagt es vorher.
 *
 * `autoListings` ist die Sammlung `auto_templates`, die das Tool schreibt
 * (docs/anfrage-personalization-tool-vorlagenliste.md). Ist sie leer oder
 * fehlt sie, kommt {covered: 0} zurueck und nichts wird markiert — die
 * Anzeige bleibt genau die von vorher.
 */
export const autoCoverage = (order, autoListings) => {
  const items = Array.isArray(order?.items) ? order.items : [];
  const known = autoListings instanceof Set ? autoListings : new Set();
  if (!items.length || !known.size) return { covered: 0, total: items.length };
  const covered = items.filter(
    (i) => known.has(String(i?.listingId || "").trim())).length;
  return { covered, total: items.length };
};

/**
 * Beschriftung der Stufe. „(auto)" nur, wenn ALLE Positionen automatisch
 * laufen — „(teils auto)" ist der ehrlichere Text, wenn eine Bestellung eine
 * Kerze mit Vorlage und ein Fotoalbum ohne enthaelt: dann muss die Grafikerin
 * eben doch ran, nur nicht fuer alles.
 */
export const stageLabelWithAuto = (stage, order, autoListings) => {
  const base = STAGE_LABELS[stage] || stage;
  if (!AUTO_RELEVANT_STAGES.has(stage)) return { label: base, auto: false };
  const { covered, total } = autoCoverage(order, autoListings);
  if (!covered) return { label: base, auto: false };
  return covered >= total
    ? { label: `${base} (auto)`, auto: true }
    : { label: `${base} (teils auto)`, auto: true };
};

// ── Badges ────────────────────────────────────────────────────────────────────

const StageBadge = ({ stage, order, autoListings }) => {
  const { label, auto } = stageLabelWithAuto(stage, order, autoListings);
  const s = auto ? AUTO_STAGE_COLOR : (STAGE_COLORS[stage] || STAGE_COLORS.new);
  return (
    <span style={{
      ...s,
      padding: "0.2em 0.65em",
      borderRadius: "12px",
      fontSize: "0.78rem",
      fontWeight: "600",
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
};

const QCBadge = () => (
  <span style={{
    background: "#fff5f5", color: "#c53030",
    border: "1px solid #fc8181",
    padding: "0.2em 0.55em",
    borderRadius: "12px",
    fontSize: "0.72rem",
    fontWeight: "600",
    whiteSpace: "nowrap",
    marginLeft: "5px",
  }}>
    ⚠ Prüfen
  </span>
);

const BounceBadge = () => (
  <span style={{
    background: "#fff5f5", color: "#c53030",
    border: "1px solid #fc8181",
    padding: "0.2em 0.55em",
    borderRadius: "12px",
    fontSize: "0.72rem",
    fontWeight: "600",
    whiteSpace: "nowrap",
    marginLeft: "5px",
  }}>
    ⚠ Bounce
  </span>
);

const DringendBadge = () => (
  <span style={{
    background: "#fffbeb", color: "#92400e",
    border: "1px solid #fbbf24",
    padding: "0.2em 0.55em",
    borderRadius: "12px",
    fontSize: "0.72rem",
    fontWeight: "600",
    whiteSpace: "nowrap",
    marginLeft: "5px",
  }}>
    ⚡ dringend
  </span>
);


// ── Expanded order detail panel ───────────────────────────────────────────────


// ── Hinweis an Kunden aus dem Auto-Entwurf ───────────────────────────────────
//
// Bewusst NUR der Hinweis: die Entwurfsliste und die Versionshistorie leben
// unter Einstellungen -> Test-Daten -> Auto-Entwuerfe. Hier steht nur, was
// beim Bearbeiten der Bestellung wichtig ist — dass der Kunde einen Hinweis
// zum Entwurf bekommen hat (z. B. gewechselte Schrift).

const CandidateNoticeBlock = ({ orderId, sectionStyle, labelStyle }) => {
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!orderId) return;
    const q = query(
      collection(db, "draft_candidates"),
      where("orderId", "==", String(orderId)),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => typeof c.customerNotice === "string"
                    && c.customerNotice.trim() !== ""
                    && c.status !== "superseded"
                    && c.status !== "rejected");
      rows.sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setNotice(rows.length ? rows[0].customerNotice.trim() : "");
    }, () => setNotice(""));
    return () => unsub();
  }, [orderId]);

  if (!notice) return null;

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>Hinweis an Kunden</div>
      <div style={{
        background: "#fff8e1", border: "1px solid #e6a545",
        borderRadius: "6px", padding: "0.6em 0.8em",
        color: "#7b341e", fontSize: "0.83rem", lineHeight: 1.45,
        whiteSpace: "pre-wrap",
      }}>{notice}</div>
    </div>
  );
};

const OrderPanel = ({ order, emailDraft, onEmailChange, isPending,
  onQCResolved, onEmailSaved }) => {
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState(null);
  const [sent, setSent]             = useState(false);
  const [stageTarget, setStageTarget]   = useState("");
  const [stageQueuing, setStageQueuing] = useState(false);
  const [stageError, setStageError]     = useState(null);
  const [stageQueued, setStageQueued]   = useState(false);
  const [firstNameInput, setFirstNameInput] = useState(
    order.confirmedFirstName || order.suggestedFirstName ||
    (order.buyerName || "").split(" ")[0] || ""
  );
  const [qcSaving, setQcSaving]     = useState(false);
  const [qcSaved, setQcSaved]       = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved]   = useState(false);
  const [nameSaving, setNameSaving]   = useState(false);
  const [nameSaved, setNameSaved]     = useState(false);
  const [shipDateInput, setShipDateInput] = useState(order.doNotShipBefore || "");
  const [shipNoteInput, setShipNoteInput] = useState(order.shippingNote || "");
  const [shipNoteSaved, setShipNoteSaved] = useState(false);

  const addr  = order.address || {};
  const items = order.items || [];
  const email = emailDraft !== undefined ? emailDraft : (order.customerEmail || "");
  const alreadySent = !!order.welcomeSentAt;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      if (email && email !== order.customerEmail) {
        await updateDoc(doc(db, "etsy_orders", order.id), { customerEmail: email });
      }
      const fn = httpsCallable(getFunctions(app, "europe-west1"), "sendWelcomeEmail");
      await fn({ orderId: order.id });
      setSent(true);
    } catch (err) {
      setError(err.message || "Fehler beim Senden.");
    } finally {
      setSending(false);
    }
  };

  const handleQueueStage = async () => {
    if (!stageTarget) return;
    setStageQueuing(true);
    setStageError(null);
    try {
      await addDoc(collection(db, "stage_changes_queue"), {
        orderId:     order.id,
        targetStage: stageTarget,
        status:      "pending",
        createdAt:   serverTimestamp(),
      });
      setStageQueued(true);
      setStageTarget("");
    } catch (err) {
      setStageError(err.message || "Fehler beim Erstellen.");
    } finally {
      setStageQueuing(false);
    }
  };

  const handleResolveQC = async () => {
    setQcSaving(true);
    try {
      const updates = {
        reviewHold: false,
        reviewIssues: [],
        confirmedFirstName: firstNameInput.trim() || null,
        emailConfirmed: true,
      };
      await updateDoc(doc(db, "etsy_orders", order.id), updates);
      setQcSaved(true);
      if (onQCResolved) onQCResolved(order.id);
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    } finally {
      setQcSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!email || email === order.customerEmail) return;
    setEmailSaving(true);
    try {
      await updateDoc(doc(db, "etsy_orders", order.id),
        { customerEmail: email, emailBounced: false });
      setEmailSaved(true);
      if (onEmailSaved) onEmailSaved(order.id, email);
      setTimeout(() => setEmailSaved(false), 3000);
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    } finally {
      setEmailSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!firstNameInput.trim()) return;
    setNameSaving(true);
    try {
      await updateDoc(doc(db, "etsy_orders", order.id),
        { confirmedFirstName: firstNameInput.trim() });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 3000);
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    } finally {
      setNameSaving(false);
    }
  };

  const handleSaveShipNote = async () => {
    try {
      await updateDoc(doc(db, "etsy_orders", order.id), {
        doNotShipBefore: shipDateInput || null,
        shippingNote: shipNoteInput || null,
      });
      setShipNoteSaved(true);
      setTimeout(() => setShipNoteSaved(false), 2000);
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    }
  };

  const panelStyle = {
    background: "#fdf7f4",
    borderTop: "1px solid #e2e8f0",
    padding: "16px 20px",
  };

  const sectionStyle = { marginBottom: "16px" };

  const labelStyle = {
    fontSize: "0.75rem",
    fontWeight: "600",
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "4px",
  };

  const isDisabled = sending || !email || alreadySent || sent ||
    (order.reviewHold && !qcSaved);

  return (
    <div style={panelStyle}>
      {/* Address */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Lieferadresse</div>
        {addr.raw ? (
          <pre style={{ margin: 0, fontFamily: "inherit", fontSize: "0.875rem",
            color: "#2d3748", whiteSpace: "pre-wrap" }}>{addr.raw}</pre>
        ) : (
          <span style={{ color: "#a0aec0", fontSize: "0.875rem" }}>—</span>
        )}
      </div>

      <CandidateNoticeBlock orderId={order.id}
        sectionStyle={sectionStyle} labelStyle={labelStyle} />

      {/* Items */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Artikel</div>
        {items.map((item, i) => (
          <div key={i} style={{
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            padding: "10px 14px",
            marginBottom: "8px",
            background: "#ffffff",
          }}>
            <div style={{ fontWeight: "600", color: "#2d3748",
              fontSize: "0.875rem", marginBottom: "4px" }}>
              {item.title}
            </div>
            {item.variant && (
              <div style={{ fontSize: "0.8rem", color: "#718096" }}>
                Variante: {item.variant}
              </div>
            )}
            <div style={{ fontSize: "0.8rem", color: "#718096" }}>
              Menge: {item.quantity || 1}
            </div>
            {(() => {
              const personFields = Array.isArray(item.personalizations) &&
                item.personalizations.length > 0
                  ? item.personalizations
                  : (item.personalization
                      ? [{ label: "Personalisierung",
                           value: item.personalization, type: "text" }]
                      : []);
              if (personFields.length === 0) return null;
              return (
                <div style={{
                  background: "#fdf7f4",
                  borderLeft: "3px solid #B8A09A",
                  padding: "8px 12px",
                  marginTop: "8px",
                  fontSize: "0.8rem",
                  color: "#2d3748",
                }}>
                  {personFields.map((p, pi) => (
                    <div key={pi} style={{
                      marginBottom: pi < personFields.length - 1
                        ? "6px" : 0,
                    }}>
                      <strong style={{ color: "#5a4a44", fontSize: "0.72rem" }}>
                        {p.label || "Personalisierung"}:
                      </strong>
                      <div style={{ whiteSpace: "pre-wrap", marginTop: "2px" }}>
                        {p.type === "file"
                          ? <a href={p.value} target="_blank"
                               rel="noopener noreferrer"
                               style={{ color: "#B8A09A",
                                        textDecoration: "underline" }}>
                              📎 Datei ansehen
                            </a>
                          : p.value}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ))}
      </div>

      {/* QC review section — only shown when reviewHold is active */}
      {order.reviewHold && !qcSaved && (
        <div style={{
          background: "#fff5f5",
          border: "1px solid #fc8181",
          borderRadius: "8px",
          padding: "14px 16px",
          marginBottom: "16px",
        }}>
          <div style={{
            fontWeight: "700", fontSize: "0.85rem", color: "#c53030",
            marginBottom: "10px",
          }}>
            ⚠ Überprüfung erforderlich — Welcome-Mail wird erst nach
            Bestätigung gesendet
          </div>

          {/* Address issue */}
          {(order.reviewIssues || []).includes("address") && (
            <div style={{
              fontSize: "0.82rem", color: "#742a2a", marginBottom: "8px",
            }}>
              <strong>Adresse:</strong> Hausnummer fehlt möglicherweise in{" "}
              &ldquo;{order.address?.street || "—"}&rdquo; — bitte prüfen
              und ggf. korrigieren.
            </div>
          )}

          {/* Name issue */}
          {(order.reviewIssues || []).includes("name") && (
            <div style={{ marginBottom: "8px" }}>
              <div style={{
                fontSize: "0.82rem", color: "#742a2a", marginBottom: "4px",
              }}>
                <strong>Vorname:</strong> &ldquo;{order.buyerName}&rdquo; —
                {order.suggestedFirstName
                  ? <> KI-Vorschlag: &ldquo;{order.suggestedFirstName}&rdquo;
                    {" "}<button
                      onClick={() => setFirstNameInput(
                        order.suggestedFirstName)}
                      style={{
                        padding: "0.1em 0.5em", fontSize: "0.75rem",
                        border: "1px solid #fc8181", borderRadius: "4px",
                        background: "#fff", color: "#c53030",
                        cursor: "pointer",
                      }}>
                      Übernehmen
                    </button>
                  </>
                  : " Titel oder unklarer Vorname erkannt."}
              </div>
              <div style={{ fontSize: "0.78rem", color: "#742a2a" }}>
                Vorname unten im Feld „Vorname für Begrüßung" eintragen und
                speichern.
              </div>
            </div>
          )}

          {/* Email issue (name-prefix fallback) */}
          {(order.reviewIssues || []).includes("email") && (
            <div style={{
              fontSize: "0.82rem", color: "#742a2a", marginBottom: "8px",
            }}>
              <strong>E-Mail:</strong>{" "}
              {order.emailMatchMethod === "missing"
                ? "Keine E-Mail-Adresse gefunden — bitte unten eintragen."
                : "E-Mail per Namensabgleich gefunden (unsicher) — bitte " +
                  "im E-Mail-Feld unten verifizieren."}
            </div>
          )}

          {/* Email mismatch (Etsy vs Firestore differ) */}
          {(order.reviewIssues || []).includes("email_mismatch") && (
            <div style={{
              fontSize: "0.82rem", color: "#742a2a", marginBottom: "8px",
            }}>
              <strong>E-Mail-Abweichung:</strong> Die gespeicherte Adresse
              weicht von der aktuellen Etsy-Adresse ab — bitte im E-Mail-Feld
              unten prüfen und ggf. korrigieren.
            </div>
          )}

          {/* Postal code issue */}
          {(order.reviewIssues || []).includes("postalCode") && (
            <div style={{
              fontSize: "0.82rem", color: "#742a2a", marginBottom: "8px",
            }}>
              <strong>PLZ:</strong> Postleitzahl fehlt oder ungültig
              {order.address?.postalCode
                ? <> — &ldquo;{order.address.postalCode}&rdquo; enthält
                  keine Ziffer</>
                : " — kein Wert vorhanden"}
              {" "}— bitte in Etsy prüfen.
            </div>
          )}

          {/* City issue */}
          {(order.reviewIssues || []).includes("city") && (
            <div style={{
              fontSize: "0.82rem", color: "#742a2a", marginBottom: "8px",
            }}>
              <strong>Stadt:</strong> Stadtname fehlt in der
              Lieferadresse — bitte in Etsy prüfen.
            </div>
          )}

          {/* Last name issue */}
          {(order.reviewIssues || []).includes("lastName") && (
            <div style={{
              fontSize: "0.82rem", color: "#742a2a", marginBottom: "8px",
            }}>
              <strong>Nachname:</strong> &ldquo;{order.buyerName}&rdquo; —
              kein Nachname erkannt. Bitte Adresse in Etsy prüfen.
            </div>
          )}

          <button
            onClick={handleResolveQC}
            disabled={qcSaving}
            style={{
              marginTop: "8px",
              padding: "0.45em 1.2em",
              borderRadius: "6px",
              border: "none",
              background: qcSaving ? "#a0aec0" : "#c53030",
              color: "#fff",
              fontWeight: "700",
              fontSize: "0.82rem",
              cursor: qcSaving ? "default" : "pointer",
            }}
          >
            {qcSaving ? "Wird gespeichert…" : "Überprüfung abschließen"}
          </button>
        </div>
      )}

      {/* Bounce warning */}
      {order.emailBounced && (
        <div style={{
          background: "#fff5f5", border: "1px solid #fc8181",
          borderRadius: "6px", padding: "10px 14px", marginBottom: "12px",
          fontSize: "0.82rem", color: "#c53030",
        }}>
          ⚠ E-Mail-Zustellung fehlgeschlagen
          {order.emailBounceType === "email.complained"
            ? " (als Spam markiert)"
            : " (Bounce)"}{" "}
          — bitte Adresse unten prüfen und korrigieren, dann
          Welcome-Mail erneut senden.
        </div>
      )}

      {/* Vorname für Begrüßung — always visible */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Vorname für Begrüßung</div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            value={firstNameInput}
            onChange={(e) => setFirstNameInput(e.target.value)}
            placeholder="Vorname"
            style={{
              flex: 1, padding: "0.4em 0.75em",
              border: "1px solid #e2e8f0", borderRadius: "6px",
              fontSize: "0.875rem", color: "#2d3748", background: "#ffffff",
            }}
          />
          <button
            onClick={handleSaveName}
            disabled={nameSaving || !firstNameInput.trim()}
            style={{
              padding: "0.4em 1em", borderRadius: "6px", border: "none",
              background: nameSaved ? "#c6f6d5"
                : (nameSaving || !firstNameInput.trim())
                  ? "#e2e8f0" : "#B8A09A",
              color: nameSaved ? "#22543d"
                : (nameSaving || !firstNameInput.trim())
                  ? "#a0aec0" : "#ffffff",
              fontWeight: "600", fontSize: "0.875rem",
              cursor: (nameSaving || !firstNameInput.trim())
                ? "default" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {nameSaved ? "✓ Gespeichert" : nameSaving ? "…" : "Speichern"}
          </button>
        </div>
        {order.confirmedFirstName && (
          <div style={{
            fontSize: "0.75rem", color: "#718096", marginTop: "3px",
          }}>
            Aktuell gespeichert: {order.confirmedFirstName}
          </div>
        )}
      </div>

      {/* Email + send button */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Kunden-E-Mail</div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="kunde@example.com"
            style={{
              flex: 1,
              padding: "0.4em 0.75em",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "0.875rem",
              color: "#2d3748",
              background: "#ffffff",
            }}
          />
          <button
            onClick={handleSaveEmail}
            disabled={emailSaving || !email || email === order.customerEmail}
            style={{
              padding: "0.4em 1em", borderRadius: "6px", border: "none",
              background: emailSaved ? "#c6f6d5"
                : (emailSaving || !email || email === order.customerEmail)
                  ? "#e2e8f0" : "#B8A09A",
              color: emailSaved ? "#22543d"
                : (emailSaving || !email || email === order.customerEmail)
                  ? "#a0aec0" : "#ffffff",
              fontWeight: "600", fontSize: "0.875rem",
              cursor: (emailSaving || !email || email === order.customerEmail)
                ? "default" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {emailSaved ? "✓" : emailSaving ? "…" : "Speichern"}
          </button>
          <button
            onClick={handleSend}
            disabled={isDisabled}
            style={{
              padding: "0.4em 1.1em",
              borderRadius: "6px",
              border: "none",
              background: isDisabled ? "#e2e8f0" : "#B8A09A",
              color: isDisabled ? "#a0aec0" : "#ffffff",
              fontWeight: "600",
              fontSize: "0.875rem",
              cursor: isDisabled ? "default" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {sending ? "Sendet…"
              : alreadySent || sent ? "Gesendet"
              : "Welcome-Mail senden"}
          </button>
        </div>
        {error && (
          <div style={{ color: "#c53030", fontSize: "0.8rem",
            marginTop: "6px" }}>
            {error}
          </div>
        )}
      </div>

      {/* Stage ändern */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Stage ändern</div>
        {isPending ? (
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#553c9a" }}>
            ⏳ Stage-Wechsel wird beim nächsten Sync ausgeführt.
          </p>
        ) : stageQueued ? (
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#22543d" }}>
            ✓ In die Queue eingereiht — wird beim nächsten Sync gesetzt.
          </p>
        ) : (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <select
              value={stageTarget}
              onChange={(e) => setStageTarget(e.target.value)}
              style={{
                flex: 1, padding: "0.4em 0.75em",
                border: "1px solid #e2e8f0", borderRadius: "6px",
                fontSize: "0.875rem", color: "#2d3748", background: "#fff",
              }}
            >
              <option value="">— Stage wählen —</option>
              {["new", "grafiker", "entwurf_raus", "produktion", "druck", "kerzen"]
                .filter((s) => s !== (order.workflowStage || "new"))
                .map((s) => (
                  <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                ))}
            </select>
            <button
              onClick={handleQueueStage}
              disabled={!stageTarget || stageQueuing}
              style={{
                padding: "0.4em 1.1em", borderRadius: "6px", border: "none",
                background: !stageTarget || stageQueuing ? "#e2e8f0" : "#B8A09A",
                color: !stageTarget || stageQueuing ? "#a0aec0" : "#ffffff",
                fontWeight: "600", fontSize: "0.875rem",
                cursor: !stageTarget || stageQueuing ? "default" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {stageQueuing ? "…" : "In Queue"}
            </button>
          </div>
        )}
        {stageError && (
          <div style={{ color: "#c53030", fontSize: "0.8rem", marginTop: "6px" }}>
            {stageError}
          </div>
        )}
      </div>

      {/* Versandhinweis */}
      <div style={sectionStyle}>
        <div style={labelStyle}>
          Versandhinweis
          {shipNoteSaved && (
            <span style={{
              color: "#22543d", fontWeight: "400",
              marginLeft: "8px", fontSize: "0.78rem",
            }}>✓ Gespeichert</span>
          )}
        </div>
        <input
          type="date"
          value={shipDateInput}
          onChange={(e) => setShipDateInput(e.target.value)}
          onBlur={handleSaveShipNote}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "0.4em 0.75em", marginBottom: "6px",
            border: "1px solid #e2e8f0", borderRadius: "6px",
            fontSize: "0.875rem", color: "#2d3748", background: "#ffffff",
          }}
        />
        <textarea
          value={shipNoteInput}
          onChange={(e) => setShipNoteInput(e.target.value)}
          onBlur={handleSaveShipNote}
          placeholder="Kommentar (z. B. Urlaub bis 20.06.)"
          rows={2}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "0.4em 0.75em",
            border: "1px solid #e2e8f0", borderRadius: "6px",
            fontSize: "0.875rem", color: "#2d3748", background: "#ffffff",
            resize: "vertical",
          }}
        />
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const SYNC_TIMEOUT_MS = 10 * 60_000;

const OrdersPage = () => {
  const [orders, setOrders]               = useState([]);
  const [syncStatus, setSyncStatus]       = useState("idle");
  const [syncCount, setSyncCount]         = useState(null);
  const [currentSyncMode, setCurrentSyncMode] = useState("scrape");
  const [gesamtsync, setGesamtsync]       = useState(null); // Date des letzten Gesamtsyncs
  const [gesamtsyncBilanzWerte, setGesamtsyncBilanzWerte] = useState(null);
  // Eigene Uhr, im Minutentakt: ohne sie bliebe „vor 3 Std." stehen, solange
  // die Seite offen ist — und die Seite bleibt hier den ganzen Tag offen.
  const [jetzt, setJetzt]                 = useState(() => new Date());
  const [expanded, setExpanded]           = useState({});
  const [emailDrafts, setEmailDrafts]     = useState({});
  const [loading, setLoading]             = useState(true);
  const [syncStale, setSyncStale]         = useState(false);
  const [cooldown, setCooldown]           = useState(null); // {until:Date, reason, challengesToday, remainingSec}
  const [syncSettings, setSyncSettings]   = useState(null); // {startHour, endHour, blockManualOutsideHours}
  const [resettingCooldown, setResettingCooldown] = useState(false);
  const [stageFilter, setStageFilter]     = useState("active"); // "active" = all except test & archived
  const [sortBy, setSortBy]               = useState("shipByDate"); // "shipByDate" | "orderDate"
  const [pendingStages, setPendingStages] = useState({}); // orderId → true
  // Artikelnummern mit Vorlage im Personalization Tool (Sammlung schreibt das
  // Tool, wir lesen nur). Heute 13 Dokumente — ein Listener ist billig.
  const [autoListings, setAutoListings]   = useState(new Set());
  const [searchQuery, setSearchQuery]     = useState("");
  const [retryingNote, setRetryingNote]   = useState({});
  const staleTimer = React.useRef(null);

  const clearStale = () => {
    setSyncStale(false);
    if (staleTimer.current) { clearTimeout(staleTimer.current); staleTimer.current = null; }
  };

  // Minutentakt nur fuer die Altersangabe des Gesamtsyncs. Firestore meldet
  // sich erst, wenn sich etwas AENDERT — ohne diese Uhr stuende „vor 3 Std."
  // auch morgen noch da, und die Anzeige wuerde genau das Gegenteil von dem
  // tun, wozu sie da ist.
  useEffect(() => {
    const t = setInterval(() => setJetzt(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const q = query(collection(db, "etsy_orders"));
    const unsubOrders = onSnapshot(q, (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    const unsubSync = onSnapshot(doc(db, "config", "orderSync"), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const status = data.status || "idle";
        setSyncStatus(status);
        if (data.mode) setCurrentSyncMode(data.mode);
        if (data.count !== undefined) setSyncCount(data.count);
        setGesamtsync(gesamtsyncZeitpunkt(data));
        setGesamtsyncBilanzWerte(gesamtsyncBilanz(data));
        if (status === "requested" || status === "running") {
          const ts = data.startedAt || data.requestedAt;
          const ageMs = ts ? (Date.now() - ts.toMillis()) : 0;
          if (ageMs > SYNC_TIMEOUT_MS) {
            setSyncStale(true);
          } else if (!staleTimer.current) {
            const remaining = Math.max(0, SYNC_TIMEOUT_MS - ageMs);
            staleTimer.current = setTimeout(() => setSyncStale(true), remaining);
          }
        } else {
          clearStale();
        }
      } else {
        setSyncStatus("idle");
        setGesamtsync(null);
        setGesamtsyncBilanzWerte(null);
        clearStale();
      }
    });

    const unsubQueue = onSnapshot(
      query(collection(db, "stage_changes_queue")),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const { orderId, status } = d.data();
          if (status === "pending") map[orderId] = true;
        });
        setPendingStages(map);
      }
    );

    // Sync-cooldown after CAPTCHA — blocks buttons + shows banner
    const unsubCooldown = onSnapshot(
      doc(db, "config", "syncCooldown"),
      (snap) => {
        if (!snap.exists()) { setCooldown(null); return; }
        const d = snap.data();
        const untilMs = d.until?.toMillis?.() || 0;
        if (!untilMs || untilMs <= Date.now()) {
          setCooldown(null);
          return;
        }
        setCooldown({
          until: new Date(untilMs),
          reason: d.reason || "bot_challenge",
          challengesToday: d.challengesToday || 0,
        });
      }
    );

    // Artikel mit Vorlage im Personalization Tool. Fehlt die Sammlung oder ist
    // sie leer, bleibt die Menge leer und die Anzeige unveraendert.
    const unsubAuto = onSnapshot(
      query(collection(db, "auto_templates")),
      (snap) => {
        setAutoListings(new Set(snap.docs
          .filter((d) => d.data()?.active !== false)
          .map((d) => String(d.data()?.listingId || d.id).trim())
          .filter(Boolean)));
      },
      () => setAutoListings(new Set()),
    );

    // Sync-settings for operating-hours + manual-block-outside-hours check
    const unsubSettings = onSnapshot(
      doc(db, "config", "syncSettings"),
      (snap) => {
        setSyncSettings(snap.exists() ? snap.data() : {});
      }
    );

    // Tick every 30s to auto-hide the banner when cooldown expires
    // (also updates the visible countdown live).
    const cdTick = setInterval(() => {
      setCooldown((prev) => {
        if (!prev) return prev;
        if (prev.until.getTime() <= Date.now()) return null;
        return { ...prev }; // trigger rerender for countdown text
      });
    }, 30_000);

    return () => {
      unsubOrders(); unsubSync(); unsubQueue(); unsubCooldown();
      unsubSettings(); unsubAuto();
      clearInterval(cdTick); clearStale();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetryNote = async (order) => {
    setRetryingNote((r) => ({ ...r, [order.id]: true }));
    try {
      const fn = httpsCallable(
        getFunctions(app, "europe-west1"),
        "retryNoteEvents",
      );
      await fn({ orderId: order.id });
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? { ...o, noteError: null, noteErrorAt: null }
            : o
        )
      );
    } catch (err) {
      alert(`Fehler beim erneuten Versuchen: ${err.message}`);
    } finally {
      setRetryingNote((r) => ({ ...r, [order.id]: false }));
    }
  };

  // Cooldown-Reset: löscht das Firestore-Doc, sofort wieder syncen möglich.
  // Wird nur mit Bestätigungsdialog ausgelöst (Risiko-Aufklärung).
  const handleResetCooldown = async () => {
    if (!cooldown) return;
    const untilStr = cooldown.until.toLocaleTimeString("de-DE", {
      hour: "2-digit", minute: "2-digit",
    });
    const confirmed = window.confirm(
      `⚠ Cooldown ZURÜCKSETZEN?\n\n` +
      `Aktuell aktiv bis ${untilStr}, Grund: ${cooldown.reason}\n` +
      `CAPTCHAs heute: ${cooldown.challengesToday}\n\n` +
      `Die Cooldown-Sperre existiert weil DataDome unseren Account als suspekt eingestuft hat.\n\n` +
      `Reset bedeutet:\n` +
      `• Sofortige Syncs wieder möglich\n` +
      `• RISIKO: nächster Sync kann sofort neuen CAPTCHA auslösen\n` +
      `• Das Etsy-Konto wird ggf. weiter geflagged (auch für andere User!)\n\n` +
      `Nur zurücksetzen wenn wirklich dringend nötig!`
    );
    if (!confirmed) return;
    setResettingCooldown(true);
    try {
      await deleteDoc(doc(db, "config", "syncCooldown"));
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    } finally {
      setResettingCooldown(false);
    }
  };

  // Shared sync trigger — enforces cooldown-override + operating-hours-block.
  // MERGE (not full set) preserves cooldownUntil/lastCompletedAt fields.
  const triggerSync = async (mode) => {
    // Operating-hours block for manual clicks (if user has enabled it).
    // endHour=0 treated as "end of day" (24). "Von 7 Bis 0" = 07:00-24:00.
    // Weekend hours (Sa+So) override weekday hours if useWeekendHours=true.
    if (syncSettings?.blockManualOutsideHours && syncSettings?.autoSyncEnabled) {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "Europe/Berlin",
        hour: "numeric",
        hour12: false,
        weekday: "short",
      }).formatToParts(new Date());
      const berlinHour = Number(parts.find(p => p.type === "hour")?.value ?? 0);
      const berlinWeekday = parts.find(p => p.type === "weekday")?.value ?? "";
      const isWeekend = berlinWeekday === "Sat" || berlinWeekday === "Sun";
      const useWeekend = !!syncSettings.useWeekendHours && isWeekend;
      const start = useWeekend
        ? (syncSettings.weekendStartHour ?? 9)
        : (syncSettings.startHour ?? 8);
      const end = useWeekend
        ? (syncSettings.weekendEndHour ?? 23)
        : (syncSettings.endHour ?? 23);
      const effectiveEnd = end === 0 ? 24 : end;
      const endDisplay = end === 0 ? "24 (Mitternacht)" : String(end);
      if (berlinHour < start || berlinHour >= effectiveEnd) {
        alert(
          `Manuelle Syncs sind außerhalb der ${useWeekend ? "Wochenend-" : ""}Betriebszeit ` +
          `(${start}-${endDisplay} Uhr Berlin) gesperrt.\n\n` +
          `Aktuelle Berlin-Stunde: ${berlinHour} (${berlinWeekday})\n\n` +
          `Änderbar unter Einstellungen → Auto-Sync.`
        );
        return;
      }
    }

    let override = false;
    if (cooldown) {
      const mins = Math.max(1, Math.round(
        (cooldown.until.getTime() - Date.now()) / 60000
      ));
      const untilStr = cooldown.until.toLocaleTimeString("de-DE", {
        hour: "2-digit", minute: "2-digit",
      });
      const confirmed = window.confirm(
        `⚠ Bot-Detection Cooldown ist aktiv.\n\n` +
        `Cooldown endet um ${untilStr} (in ~${mins} min).\n` +
        `Grund: ${cooldown.reason}\n` +
        `CAPTCHAs heute: ${cooldown.challengesToday}\n\n` +
        `Trotzdem starten? Kann weitere Bot-Detection auslösen.`
      );
      if (!confirmed) return;
      override = true;
    }
    setSyncCount(null);
    clearStale();
    await setDoc(doc(db, "config", "orderSync"), {
      status: "requested",
      mode,
      requestedAt: serverTimestamp(),
      ...(override ? { overrideCooldown: true } : {}),
    }, { merge: true });
  };

  const handleSyncAllgemein = () => triggerSync("scrape");
  const handleSyncShipping  = () => triggerSync("shipping");
  // 2026-08-31: Bewertungen-Button entfernt — Reviews werden von
  // pollEtsyNewReviews CF alle 30 Min automatisch geholt und approved
  // Reviews vom Scraper am Ende jedes Sync-Laufs automatisch gepostet.
  // handleSyncReviews bleibt als eslint-suppressed no-op, falls jemand
  // die Route noch direkt callt.

  const handleCancelSync = async () => {
    clearStale();
    // MERGE to preserve cooldownUntil/lastCompletedAt fields.
    await setDoc(doc(db, "config", "orderSync"),
      { status: "idle" },
      { merge: true },
    );
  };

  const toggleExpanded = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const isSyncing = syncStatus === "requested" || syncStatus === "running";

  const sortedOrders = [...orders].sort((a, b) => {
    if (sortBy === "orderDate") {
      // Newest order date first
      return (b.orderDate || "").localeCompare(a.orderDate || "");
    }
    // Most urgent ship date first (ascending), missing dates go to end
    const da = a.shipByDate || "9999";
    const db2 = b.shipByDate || "9999";
    return da.localeCompare(db2);
  });

  const ACTIVE_STAGES = new Set(["new", "grafiker", "entwurf_raus", "produktion", "druck", "kerzen"]);

  const stageFiltered = stageFilter === null
    ? sortedOrders
    : stageFilter === "active"
      ? sortedOrders.filter((o) => ACTIVE_STAGES.has(o.workflowStage || "new"))
      : sortedOrders.filter((o) => (o.workflowStage || "new") === stageFilter);

  const qcHoldOrders   = orders.filter((o) => o.reviewHold && o.workflowStage !== "archived");
  const qcHoldCount    = qcHoldOrders.length;
  const bounceOrders   = orders.filter((o) => o.emailBounced && o.workflowStage !== "archived");
  const bounceCount    = bounceOrders.length;

  const q = searchQuery.trim().toLowerCase();
  const visibleOrders = q
    ? stageFiltered.filter((o) =>
        (o.buyerName || "").toLowerCase().includes(q) ||
        (o.id || "").toLowerCase().includes(q) ||
        (o.customerEmail || "").toLowerCase().includes(q)
      )
    : stageFiltered;

  // Count per stage for filter pills
  const stageCounts = {};
  for (const o of orders) {
    const s = o.workflowStage || "new";
    stageCounts[s] = (stageCounts[s] || 0) + 1;
  }

  const headStyle = {
    background: "#f7fafc",
    color: "#4a5568",
    fontWeight: "600",
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "10px 14px",
    textAlign: "left",
    borderBottom: "1px solid #e2e8f0",
  };

  const cellStyle = {
    padding: "12px 14px",
    fontSize: "0.875rem",
    color: "#2d3748",
    borderBottom: "1px solid #f0f4f8",
    verticalAlign: "middle",
  };

  // ── Cooldown banner data (rendered above header) ───────────────────────────
  const renderCooldownBanner = () => {
    if (!cooldown) return null;
    const remainingMs = Math.max(0, cooldown.until.getTime() - Date.now());
    const totalMin = Math.ceil(remainingMs / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const remainStr = h > 0 ? `${h}h ${m}min` : `${m}min`;
    const untilStr = cooldown.until.toLocaleTimeString("de-DE", {
      hour: "2-digit", minute: "2-digit",
    });
    return (
      <div style={{
        background: "#fef3c7", border: "1px solid #f59e0b",
        borderRadius: "8px", padding: "12px 16px", marginBottom: "1em",
        display: "flex", alignItems: "center", gap: "12px",
      }}>
        <span style={{ fontSize: "1.4rem" }}>⚠</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, color: "#78350f", fontSize: "0.95rem" }}>
            Sync-Cooldown aktiv — Bot-Detection erkannt
          </div>
          <div style={{ fontSize: "0.85rem", color: "#78350f", marginTop: "2px" }}>
            Auto-Sync gesperrt bis {untilStr} (in {remainStr}) ·
            CAPTCHAs heute: {cooldown.challengesToday}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#78350f", marginTop: "4px" }}>
            Bitte CAPTCHA im Chrome-Fenster des Scraper-PCs lösen.
            Manuelles Starten möglich, aber Klick zeigt Bestätigungsdialog.
          </div>
        </div>
        <button
          onClick={handleResetCooldown}
          disabled={resettingCooldown}
          style={{
            padding: "0.55em 1em",
            borderRadius: "6px",
            border: "1px solid #b45309",
            background: resettingCooldown ? "#fef3c7" : "#fff",
            color: "#78350f",
            fontWeight: 600,
            fontSize: "0.85rem",
            cursor: resettingCooldown ? "default" : "pointer",
            whiteSpace: "nowrap",
          }}
          title="Cooldown-Sperre aufheben (mit Bestätigungsdialog)"
        >
          {resettingCooldown ? "…" : "Cooldown zurücksetzen"}
        </button>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      {renderCooldownBanner()}
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: "1em" }}>
        <div>
          <h2 style={{ margin: 0, color: "#1a202c", fontSize: "1.25rem" }}>
            Etsy Bestellungen
          </h2>
          <span style={{ fontSize: "0.8rem", color: "#a0aec0" }}>
            {orders.length} Bestellung{orders.length !== 1 ? "en" : ""}
          </span>
        </div>

        <div style={{
          display: "flex", flexDirection: "column",
          alignItems: "flex-end", gap: "5px",
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {syncStatus === "done" && syncCount !== null && (
            <span style={{ fontSize: "0.85rem", color: "#22543d" }}>
              {syncCount} neue Bestellung{syncCount !== 1 ? "en" : ""} importiert
            </span>
          )}
          {syncStatus === "error" && (
            <span style={{ fontSize: "0.85rem", color: "#c53030" }}>
              Sync-Fehler — Scraper-Terminal prüfen
            </span>
          )}
          {syncStale && (
            <span style={{ fontSize: "0.8rem", color: "#c53030" }}>
              Scraper läuft nicht?{" "}
              <button onClick={handleCancelSync} style={{
                background: "none", border: "none", color: "#c53030",
                textDecoration: "underline", cursor: "pointer",
                fontSize: "0.8rem", padding: 0,
              }}>
                Abbrechen
              </button>
            </span>
          )}
          {[
            { label: "Allgemein",  mode: "scrape",   handler: handleSyncAllgemein },
            { label: "Versand",    mode: "shipping",  handler: handleSyncShipping },
          ].map(({ label, mode, handler }) => {
            const active = isSyncing && !syncStale && currentSyncMode === mode;
            const disabled = isSyncing && !syncStale;
            return (
              <button
                key={mode}
                onClick={handler}
                disabled={disabled}
                style={{
                  padding: "0.5em 1.1em",
                  borderRadius: "6px",
                  border: "none",
                  background: disabled ? "#e2e8f0" : "#B8A09A",
                  color: disabled ? "#a0aec0" : "#ffffff",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  cursor: disabled ? "default" : "pointer",
                }}
              >
                {active ? "Synchronisiert…" : label}
              </button>
            );
          })}
        </div>

        {/* Wann wurden zuletzt ALLE Stufen abgeglichen (Wunsch Tobias,
            04.10.2026). Steht absichtlich unter „Allgemein" — das ist der
            Knopf, der einen Gesamtsync auslöst. */}
        <GesamtsyncZeile datum={gesamtsync} jetzt={jetzt}
                         bilanz={gesamtsyncBilanzWerte} />
        </div>
      </div>

      {/* Stage filter pills */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "1em" }}>
        {/* Aktiv — default filter, leftmost */}
        <button
          onClick={() => setStageFilter("active")}
          style={{
            padding: "0.3em 0.9em",
            borderRadius: "20px",
            border: stageFilter === "active" ? "2px solid #B8A09A" : "1px solid #e2e8f0",
            background: stageFilter === "active" ? "#fdf7f4" : "#ffffff",
            color: stageFilter === "active" ? "#744210" : "#718096",
            fontWeight: stageFilter === "active" ? "700" : "400",
            fontSize: "0.8rem",
            cursor: "pointer",
          }}
        >
          Aktiv ({orders.filter((o) => ACTIVE_STAGES.has(o.workflowStage || "new")).length})
        </button>
        {/* Individual stage pills */}
        {STAGE_KEYS.map((key) => {
          const count = stageCounts[key] || 0;
          if (count === 0) return null;
          const active = stageFilter === key;
          const c = STAGE_COLORS[key];
          return (
            <button
              key={key}
              onClick={() => setStageFilter(active ? "active" : key)}
              style={{
                padding: "0.3em 0.9em",
                borderRadius: "20px",
                border: active ? `2px solid ${c.color}` : "1px solid #e2e8f0",
                background: active ? c.background : "#ffffff",
                color: active ? c.color : "#718096",
                fontWeight: active ? "700" : "400",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              {STAGE_LABELS[key]} ({count})
            </button>
          );
        })}
        {/* Alle — rightmost */}
        <button
          onClick={() => setStageFilter(null)}
          style={{
            padding: "0.3em 0.9em",
            borderRadius: "20px",
            border: stageFilter === null ? "2px solid #B8A09A" : "1px solid #e2e8f0",
            background: stageFilter === null ? "#fdf7f4" : "#ffffff",
            color: stageFilter === null ? "#744210" : "#718096",
            fontWeight: stageFilter === null ? "700" : "400",
            fontSize: "0.8rem",
            cursor: "pointer",
          }}
        >
          Alle ({orders.length})
        </button>
      </div>

      {/* Sort toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "0.75em" }}>
        <span style={{ fontSize: "0.78rem", color: "#a0aec0", marginRight: "2px" }}>
          Sortierung:
        </span>
        {[
          { key: "shipByDate", label: "Versand bis (dringend zuerst)" },
          { key: "orderDate",  label: "Bestelldatum (neueste zuerst)" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setSortBy(key)}
            style={{
              padding: "0.25em 0.8em",
              borderRadius: "6px",
              border: sortBy === key ? "2px solid #B8A09A" : "1px solid #e2e8f0",
              background: sortBy === key ? "#fdf7f4" : "#ffffff",
              color: sortBy === key ? "#744210" : "#718096",
              fontWeight: sortBy === key ? "700" : "400",
              fontSize: "0.78rem",
              cursor: "pointer",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: "0.75em", position: "relative" }}>
        <input
          type="search"
          placeholder="Suche nach Name, Bestellnummer, E-Mail …"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "0.5em 0.75em 0.5em 2.2em",
            border: "1px solid #e2e8f0", borderRadius: "8px",
            fontSize: "0.875rem", color: "#2d3748", background: "#fff",
            outline: "none",
          }}
        />
        <span style={{
          position: "absolute", left: "0.65em", top: "50%",
          transform: "translateY(-50%)", fontSize: "0.9rem", color: "#a0aec0",
          pointerEvents: "none",
        }}>🔍</span>
      </div>

      {/* QC hold warning banner */}
      {qcHoldCount > 0 && (
        <div style={{
          background: "#fff5f5",
          border: "1px solid #fc8181",
          borderRadius: "8px",
          padding: "0.75em 1.1em",
          marginBottom: "1em",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.5em",
        }}>
          <span style={{ fontSize: "1.1rem" }}>⚠</span>
          <span style={{ color: "#c53030", fontWeight: "600", fontSize: "0.88rem" }}>
            {qcHoldCount > 1 ? `${qcHoldCount} Bestellungen benötigen` : "Bestellung benötigt"}{" "}
            Überprüfung — Adresse, Vorname oder E-Mail prüfen.
            Welcome-Mail wird erst nach Bestätigung gesendet.
          </span>
          {qcHoldOrders.map((o) => (
            <button
              key={o.id}
              onClick={() => { setStageFilter(null); setSearchQuery(o.id); }}
              style={{
                padding: "0.15em 0.6em", borderRadius: "6px",
                border: "1px solid #fc8181", background: "#fff",
                color: "#c53030", fontWeight: "700", fontSize: "0.82rem",
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              #{o.id}
            </button>
          ))}
        </div>
      )}

      {/* Bounce warning banner */}
      {bounceCount > 0 && (
        <div style={{
          background: "#fff5f5",
          border: "1px solid #fc8181",
          borderRadius: "8px",
          padding: "0.75em 1.1em",
          marginBottom: "1em",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.5em",
        }}>
          <span style={{ fontSize: "1.1rem" }}>⚠</span>
          <span style={{ color: "#c53030", fontWeight: "600", fontSize: "0.88rem" }}>
            {bounceCount > 1 ? `${bounceCount} Bestellungen` : "Bestellung"} mit
            fehlgeschlagener E-Mail-Zustellung — E-Mail-Adresse prüfen
            und ggf. korrigieren.
          </span>
          {bounceOrders.map((o) => (
            <button
              key={o.id}
              onClick={() => { setStageFilter(null); setSearchQuery(o.id); }}
              style={{
                padding: "0.15em 0.6em", borderRadius: "6px",
                border: "1px solid #fc8181", background: "#fff",
                color: "#c53030", fontWeight: "700", fontSize: "0.82rem",
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              #{o.id}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{
        background: "#ffffff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}>
        {loading ? (
          <div style={{ padding: "3em", textAlign: "center", color: "#a0aec0" }}>
            Lädt…
          </div>
        ) : visibleOrders.length === 0 ? (
          <div style={{ padding: "3em", textAlign: "center", color: "#a0aec0" }}>
            {orders.length === 0
              ? "Noch keine Bestellungen — klicke Sync um Etsy-Bestellungen zu importieren."
              : "Keine Bestellungen in dieser Stage."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse",
            minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={headStyle}>Versand bis</th>
                <th style={headStyle}>Bestelldatum</th>
                <th style={headStyle}>Bestellung #</th>
                <th style={headStyle}>Käufer</th>
                <th style={headStyle}>Stage</th>
                <th style={headStyle}>Welcome</th>
                <th style={headStyle}>Auftrag</th>
                <th style={headStyle}>Entwurf</th>
                <th style={headStyle}>Versand</th>
                <th style={headStyle}>Notiz</th>
                <th style={{ ...headStyle, width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <tr
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleExpanded(order.id)}
                  >
                    <td style={{
                      ...cellStyle,
                      color: isUrgent(order.shipByDate) ? "#c53030" : "#2d3748",
                      fontWeight: isUrgent(order.shipByDate) ? "600" : "400",
                    }}>
                      {formatIsoDate(order.shipByDate)}
                    </td>
                    <td style={{ ...cellStyle, color: "#718096" }}>
                      {formatIsoDate(order.orderDate)}
                    </td>
                    <td style={{ ...cellStyle, color: "#718096" }}>
                      #{order.orderId || order.id}
                    </td>
                    <td style={cellStyle}>{order.buyerName || "—"}</td>
                    <td style={cellStyle}>
                      <StageBadge stage={order.workflowStage || "new"}
                        order={order} autoListings={autoListings} />
                      {order.reviewHold && <QCBadge />}
                      {order.emailBounced && <BounceBadge />}
                      {order.isDringend && <DringendBadge />}
                      {pendingStages[order.id] && (
                        <span style={{
                          background: "#faf5ff", color: "#553c9a",
                          border: "1px solid #b794f4",
                          padding: "0.2em 0.55em", borderRadius: "12px",
                          fontSize: "0.72rem", fontWeight: "600",
                          marginLeft: "5px", whiteSpace: "nowrap",
                        }}>
                          ⏳ ausstehend
                        </span>
                      )}
                    </td>
                    {/* Status — welcome sent */}
                    <td style={cellStyle}>
                      {order.welcomeSentAt && (
                        <span style={{
                          background: "#276749", color: "#ffffff",
                          border: "1px solid #276749",
                          padding: "0.2em 0.55em", borderRadius: "12px",
                          fontSize: "0.72rem", fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}>
                          Begrüßt
                        </span>
                      )}
                    </td>
                    {/* Auftrag — order review decision */}
                    <td style={cellStyle}>
                      {order.orderDecision === "confirmed" && (
                        <span style={{
                          background: "#276749", color: "#ffffff",
                          border: "1px solid #276749",
                          padding: "0.2em 0.55em", borderRadius: "12px",
                          fontSize: "0.72rem", fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}>
                          ✓ Bestätigt
                        </span>
                      )}
                      {order.orderDecision === "modification" && (
                        <span
                          title={order.orderFeedback || ""}
                          style={{
                            background: "#fffbeb", color: "#b45309",
                            border: "1px solid #fcd34d",
                            padding: "0.2em 0.55em", borderRadius: "12px",
                            fontSize: "0.72rem", fontWeight: "600",
                            whiteSpace: "nowrap", cursor: "help",
                          }}
                        >
                          ✎ Anmerkung
                        </span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      {order.draftApprovedAt && (
                        <span style={{
                          background: "#276749", color: "#ffffff",
                          border: "1px solid #276749",
                          padding: "0.2em 0.55em", borderRadius: "12px",
                          fontSize: "0.72rem", fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}>
                          ✓ Freigegeben
                        </span>
                      )}
                      {!order.draftApprovedAt && order.draftDecision === "modification" && (
                        <span
                          title={order.draftFeedback || ""}
                          style={{
                            background: "#fffbeb", color: "#b45309",
                            border: "1px solid #fcd34d",
                            padding: "0.2em 0.55em", borderRadius: "12px",
                            fontSize: "0.72rem", fontWeight: "600",
                            whiteSpace: "nowrap", cursor: "help",
                          }}
                        >
                          ✎ Anmerkung
                        </span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      {order.labelCreatedAt && (
                        <span
                          title={[
                            order.trackingNumber
                              ? `Tracking: ${order.trackingNumber}`
                              : null,
                            order.labelCreatedAt?.toDate
                              ? `Erstellt: ${order.labelCreatedAt
                                  .toDate()
                                  .toLocaleString("de-DE")}`
                              : null,
                          ].filter(Boolean).join("\n")}
                          style={{
                            background: "#f0fff4", color: "#276749",
                            border: "1px solid #9ae6b4",
                            padding: "0.2em 0.55em", borderRadius: "12px",
                            fontSize: "0.72rem", fontWeight: "600",
                            whiteSpace: "nowrap", cursor: "default",
                          }}
                        >
                          📦 Etikett
                        </span>
                      )}
                      {order.deliveredAt && (
                        <span
                          title={order.deliveredAt?.toDate
                            ? `Zugestellt: ${order.deliveredAt.toDate().toLocaleString("de-DE")}`
                            : "Zugestellt"}
                          style={{
                            background: "#e6fffa", color: "#234e52",
                            border: "1px solid #81e6d9",
                            padding: "0.2em 0.55em", borderRadius: "12px",
                            fontSize: "0.72rem", fontWeight: "600",
                            whiteSpace: "nowrap", cursor: "default", marginLeft: "4px",
                          }}
                        >
                          📬 Zugestellt
                        </span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      {order.noteError && (
                        <>
                          <span
                            title="Etsy-Privatnotiz konnte nicht geschrieben werden"
                            style={{
                              padding: "0.2em 0.45em", borderRadius: "12px",
                              border: "1px solid #fc8181",
                              background: "#fff5f5", color: "#c53030",
                              fontSize: "0.72rem", fontWeight: "600",
                              whiteSpace: "nowrap", cursor: "help",
                            }}
                          >
                            ⚠ Notiz Fehler
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRetryNote(order);
                            }}
                            disabled={!!retryingNote[order.id]}
                            style={{
                              marginLeft: "0.4em", padding: "0.1em 0.5em",
                              borderRadius: "12px",
                              border: "1px solid #90cdf4",
                              background: "#ebf8ff", color: "#2b6cb0",
                              fontSize: "0.72rem", fontWeight: "600",
                              whiteSpace: "nowrap",
                              cursor: retryingNote[order.id]
                                ? "default" : "pointer",
                              opacity: retryingNote[order.id] ? 0.6 : 1,
                            }}
                          >
                            {retryingNote[order.id] ? "…" : "↺ Erneut"}
                          </button>
                        </>
                      )}
                    </td>
                    <td style={{ ...cellStyle, textAlign: "center",
                      color: "#a0aec0", fontSize: "1rem" }}>
                      {expanded[order.id] ? "▲" : "▼"}
                    </td>
                  </tr>
                  {expanded[order.id] && (
                    <tr>
                      <td colSpan={11} style={{ padding: 0 }}>
                        <OrderPanel
                          order={order}
                          emailDraft={emailDrafts[order.id]}
                          onEmailChange={(val) =>
                            setEmailDrafts((prev) => (
                              { ...prev, [order.id]: val }))}
                          isPending={!!pendingStages[order.id]}
                          onQCResolved={(id) =>
                            setOrders((prev) => prev.map((o) =>
                              o.id === id
                                ? { ...o, reviewHold: false,
                                    reviewIssues: [] }
                                : o
                            ))
                          }
                          onEmailSaved={(id, newEmail) =>
                            setOrders((prev) => prev.map((o) =>
                              o.id === id
                                ? { ...o, customerEmail: newEmail }
                                : o
                            ))
                          }
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Scraper hint */}
      <p style={{ marginTop: "1em", fontSize: "0.78rem", color: "#a0aec0" }}>
        Der lokale Scraper muss im Terminal laufen:{" "}
        <code>py scraper/main.py --shop {process.env.REACT_APP_SHOP_NAME?.toLowerCase() || "printsonalities"} --mode watch</code>
      </p>
    </div>
  );
};

export default OrdersPage;
